import React, { useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { FaCamera, FaUpload, FaVideo, FaLock, FaUser, FaEnvelope } from 'react-icons/fa';
import './RegisterPage.css';

function RegisterPage() {
  const [formData, setFormData] = useState({
    // E-waste fields
    first_name: '',
    middle_name: '',
    last_name: '',
    suffix: '',
    address: '',
    age: '',
    contact: '',
    e_waste_type: '',
    weight: '',
    photo_url: '',
    consent: false,
    // User credentials
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    security_question: 'What is your mother\'s name?',
    security_answer: ''
  });

  const [photoPreview, setPhotoPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);

  const securityQuestions = [
    'What is your mother\'s name?',
    'What is your pet\'s name?',
    'What is your favorite color?',
    'What is your favorite food?',
    'What city were you born in?',
    'What is your favorite movie?',
    'What is your favorite book?',
    'What is your favorite sports team?'
  ];

  const eWasteTypes = [
    'Desktop Computer', 'Laptop', 'Smartphone', 'Tablet', 'Monitor',
    'Keyboard/Mouse', 'Cables & Chargers', 'CPU/Processor', 'Motherboard',
    'RAM', 'Hard Drive', 'Power Supply', 'Printer', 'Scanner', 'Television', 'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    // E-waste field validation
    if (['first_name', 'last_name'].includes(name)) {
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (name === 'middle_name') {
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (name === 'suffix') {
      finalValue = value.replace(/[^a-zA-Z\s.]/g, '');
    }
    if (name === 'address') {
      finalValue = value.replace(/[^a-zA-Z\s,\-]/g, '');
    }
    if (name === 'age') {
      finalValue = value.replace(/[^0-9]/g, '').slice(0, 2);
    }
    if (name === 'contact') {
      finalValue = value.replace(/[^0-9]/g, '').slice(0, 11);
    }
    if (name === 'username') {
      finalValue = value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 20);
    }

    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const resizeImage = (file, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = Math.min(img.width, img.height);
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 200, 200);
        canvas.toBlob(callback, 'image/jpeg', 0.9);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire({ icon: 'error', title: 'Invalid File', text: 'Please upload an image file', confirmButtonColor: '#10b981' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: 'error', title: 'File Too Large', text: 'Max 5MB', confirmButtonColor: '#10b981' });
      return;
    }

    setUploading(true);
    resizeImage(file, async (resizedBlob) => {
      try {
        const formDataCloud = new FormData();
        formDataCloud.append('file', resizedBlob);
        formDataCloud.append('upload_preset', 'ecyclehub_preset');

        const response = await fetch('https://api.cloudinary.com/v1_1/dtr1tnutd/image/upload', {
          method: 'POST',
          body: formDataCloud
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({ ...prev, photo_url: data.secure_url }));
          setPhotoPreview(data.secure_url);
          setUploading(false);
          Swal.fire({ icon: 'success', title: '✓ Photo Uploaded!', html: '<p>Your 2x2 photo is ready</p>', confirmButtonColor: '#10b981', timer: 2000 });
        } else {
          throw new Error('Upload failed');
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Upload Failed', text: err.message, confirmButtonColor: '#10b981' });
        setUploading(false);
      }
    });
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setShowCamera(true);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Camera Error', text: 'Could not access camera', confirmButtonColor: '#10b981' });
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      context.drawImage(videoRef.current, 0, 0, 200, 200);
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) return;
        const formDataCloud = new FormData();
        formDataCloud.append('file', blob);
        formDataCloud.append('upload_preset', 'ecyclehub_preset');
        setUploading(true);
        try {
          const response = await fetch('https://api.cloudinary.com/v1_1/dtr1tnutd/image/upload', {
            method: 'POST',
            body: formDataCloud
          });
          if (response.ok) {
            const data = await response.json();
            setFormData(prev => ({ ...prev, photo_url: data.secure_url }));
            setPhotoPreview(data.secure_url);
            stopCamera();
            setUploading(false);
            Swal.fire({ icon: 'success', title: '✓ Photo Captured!', html: '<p>Your 2x2 photo is ready</p>', confirmButtonColor: '#10b981', timer: 2000 });
          } else {
            throw new Error('Upload failed');
          }
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Upload Failed', text: err.message, confirmButtonColor: '#10b981' });
          setUploading(false);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setShowCamera(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // E-waste validation
    if (!formData.first_name || !formData.last_name || !formData.address || !formData.age || !formData.contact || !formData.e_waste_type || !formData.weight || !formData.photo_url || !formData.consent) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please fill all required e-waste fields', confirmButtonColor: '#10b981' });
      return;
    }

    if (/[0-9]/.test(formData.address)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Address', text: 'Address cannot contain numbers', confirmButtonColor: '#10b981' });
      return;
    }

    if (formData.age < 18 || formData.age > 99) {
      Swal.fire({ icon: 'warning', title: 'Invalid Age', text: 'Age must be 18-99', confirmButtonColor: '#10b981' });
      return;
    }

    if (formData.contact.length !== 11) {
      Swal.fire({ icon: 'warning', title: 'Invalid Contact', text: 'Contact must be 11 digits', confirmButtonColor: '#10b981' });
      return;
    }

    // Credentials validation
    if (!formData.username || formData.username.length < 3 || formData.username.length > 20) {
      Swal.fire({ icon: 'warning', title: 'Invalid Username', text: 'Username must be 3-20 characters', confirmButtonColor: '#10b981' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({ icon: 'warning', title: 'Invalid Email', text: 'Please enter a valid email', confirmButtonColor: '#10b981' });
      return;
    }

    if (formData.password.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Weak Password', text: 'Password must be at least 6 characters', confirmButtonColor: '#10b981' });
      return;
    }

    if (formData.password !== formData.confirm_password) {
      Swal.fire({ icon: 'warning', title: 'Passwords Do Not Match', text: 'Please confirm your password', confirmButtonColor: '#10b981' });
      return;
    }

    if (!formData.security_answer.trim() || formData.security_answer.trim().length < 2) {
      Swal.fire({ icon: 'warning', title: 'Invalid Security Answer', text: 'Please answer the security question', confirmButtonColor: '#10b981' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/registrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        const reward = formData.weight * 15;
        const fullName = `${formData.first_name} ${formData.middle_name ? formData.middle_name + ' ' : ''}${formData.last_name}${formData.suffix ? ' ' + formData.suffix : ''}`;

        Swal.fire({
          icon: 'success',
          title: '✓ Registration Successful!',
          html: `
            <div style="text-align: center;">
              <p style="font-size: 16px; margin: 10px 0;"><strong>Welcome, ${formData.first_name}!</strong></p>
              <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; margin: 15px 0;">
                <p style="margin: 8px 0;"><strong>Name:</strong> ${fullName}</p>
                <p style="margin: 8px 0;"><strong>Username:</strong> ${formData.username}</p>
                <p style="margin: 8px 0;"><strong>E-Waste:</strong> ${formData.e_waste_type}</p>
                <p style="margin: 8px 0;"><strong>Weight:</strong> ${formData.weight} kg</p>
                <p style="margin: 8px 0; font-size: 18px; color: #10b981;"><strong>Reward: ₱${reward.toFixed(2)}</strong></p>
              </div>
              <p style="font-size: 14px; color: #666;">You can now login with your username and password!</p>
            </div>
          `,
          confirmButtonColor: '#10b981',
          confirmButtonText: 'Go to Login'
        }).then(() => {
          window.location.href = '/login';
        });

        setFormData({
          first_name: '', middle_name: '', last_name: '', suffix: '', address: '', age: '', contact: '',
          e_waste_type: '', weight: '', photo_url: '', consent: false,
          username: '', email: '', password: '', confirm_password: '', security_question: 'What is your mother\'s name?', security_answer: ''
        });
        setPhotoPreview('');
      } else {
        const error = await response.json();
        Swal.fire({ icon: 'error', title: 'Registration Failed', text: error.error, confirmButtonColor: '#10b981' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Registration failed. Please try again.', confirmButtonColor: '#10b981' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Register Your E-Waste Drop-off</h1>
          <p className="text-emerald-100 text-lg">Create your account and register e-waste</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* ========== PHOTO UPLOAD SECTION ========== */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border-2 border-emerald-200">
              <label className="block text-lg font-bold text-gray-900 mb-4">
                <FaCamera className="inline mr-2 text-emerald-600" />
                Upload Your 2x2 Photo *
              </label>
              
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="w-40 h-40 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-emerald-300">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <FaCamera className="text-5xl text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">No photo</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  {!showCamera ? (
                    <div className="space-y-3">
                      <input type="file" id="photo" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} className="hidden" />
                      <label htmlFor="photo" className="block">
                        <div className="border-2 border-dashed border-emerald-400 rounded-lg p-6 text-center cursor-pointer hover:bg-emerald-50 transition-colors">
                          <FaUpload className="text-3xl text-emerald-600 mx-auto mb-2" />
                          <p className="text-sm font-semibold text-gray-900">Click to upload photo</p>
                          <p className="text-xs text-gray-600 mt-1">PNG, JPG, GIF up to 5MB</p>
                        </div>
                      </label>
                      <button type="button" onClick={startCamera} disabled={uploading} className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        <FaVideo /> Take Photo with Camera
                      </button>
                      {uploading && <p className="text-sm text-emerald-600">Uploading...</p>}
                      {formData.photo_url && <p className="text-sm text-emerald-600">✓ Photo uploaded (200x200px)</p>}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg border-2 border-emerald-400" style={{ maxHeight: '300px' }} />
                      <canvas ref={canvasRef} width="200" height="200" className="hidden" />
                      <div className="flex gap-2">
                        <button type="button" onClick={capturePhoto} disabled={uploading} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                          Capture Photo
                        </button>
                        <button type="button" onClick={stopCamera} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ========== E-WASTE INFORMATION ========== */}
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">E-Waste Information</h2>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Juan" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Middle Name</label>
                  <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} placeholder="Dela" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Cruz" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Suffix</label>
                  <input type="text" name="suffix" value={formData.suffix} onChange={handleChange} placeholder="Jr., Sr., III" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address * (NO NUMBERS)</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Waterfall Balingasag Mis Or" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Age (18-99) *</label>
                  <input type="number" name="age" value={formData.age} onChange={handleChange} placeholder="21" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact (11 digits) *</label>
                  <input type="tel" name="contact" value={formData.contact} onChange={handleChange} placeholder="09856122843" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Weight (kg) *</label>
                  <input type="number" name="weight" value={formData.weight} onChange={handleChange} placeholder="100" step="0.1" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">E-Waste Type *</label>
                <select name="e_waste_type" value={formData.e_waste_type} onChange={handleChange} className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all" required>
                  <option value="">Select E-Waste Type</option>
                  {eWasteTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
            </div>

            {/* ========== USER CREDENTIALS ========== */}
            <div className="bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaUser className="text-purple-600" /> Create Your Account
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Username * (3-20 characters)</label>
                  <input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="john_doe" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FaEnvelope className="text-purple-600" /> Email *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FaLock className="text-purple-600" /> Password * (min 6 characters)
                  </label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FaLock className="text-purple-600" /> Confirm Password *
                  </label>
                  <input type="password" name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="••••••" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Security Question *</label>
                  <select name="security_question" value={formData.security_question} onChange={handleChange} className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required>
                    {securityQuestions.map(q => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Security Answer *</label>
                  <input type="text" name="security_answer" value={formData.security_answer} onChange={handleChange} placeholder="Your answer" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all" required />
                </div>
              </div>
            </div>

            {/* ========== CONSENT ========== */}
            <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} className="w-5 h-5 mt-1 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500" required />
                <span className="text-sm text-gray-700">
                  I consent to the collection and processing of my e-waste and agree to the terms and conditions *
                </span>
              </label>
            </div>

            {/* ========== SUBMIT BUTTON ========== */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? 'Registering...' : 'Submit Registration'}
            </button>

            <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
              <p className="text-sm text-green-800">
                <strong>Note:</strong> Your information will be kept confidential and used only for e-waste collection purposes. We appreciate your contribution to environmental sustainability!
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
